/**
 * SVG Prefetch utility for eSIM card images
 * Preloads SVG images to prevent flickering when rendering phone number cards
 */

import { getNftSvgUrl, type SvgLoadType } from '../components/esim-card-background';
import { imageCache } from './image-cache';

interface PrefetchEntry {
	url: string;
	status: 'pending' | 'loading' | 'loaded' | 'error';
	promise?: Promise<void>;
}

class SvgPrefetchManager {
	private prefetchQueue: Map<string, PrefetchEntry> = new Map();
	private concurrentLimit = 4;
	private activeLoads = 0;
	private pendingQueue: string[] = [];

	/**
	 * Normalize phone number to tokenId format (digits only)
	 */
	private normalizeToTokenId(phoneNumber: string): string {
		return phoneNumber.replace(/\D/g, '');
	}

	/**
	 * Get SVG URL for a phone number
	 */
	private getSvgUrl(phoneNumber: string, type: SvgLoadType): string | null {
		const tokenId = this.normalizeToTokenId(phoneNumber);
		if (!tokenId) return null;
		return getNftSvgUrl(tokenId, type);
	}

	/**
	 * Preload a single SVG image
	 */
	private preloadSvg(url: string): Promise<void> {
		if (imageCache.isLikelyCached(url)) {
			return Promise.resolve();
		}

		const existing = this.prefetchQueue.get(url);
		if (existing?.promise) {
			return existing.promise;
		}

		const promise = new Promise<void>((resolve, reject) => {
			const img = new Image();

			img.onload = () => {
				imageCache.setCached(url, true);
				const entry = this.prefetchQueue.get(url);
				if (entry) {
					entry.status = 'loaded';
				}
				this.activeLoads--;
				this.processQueue();
				resolve();
			};

			img.onerror = () => {
				imageCache.setCached(url, false, true);
				const entry = this.prefetchQueue.get(url);
				if (entry) {
					entry.status = 'error';
				}
				this.activeLoads--;
				this.processQueue();
				reject(new Error(`Failed to preload SVG: ${url}`));
			};

			img.src = url;
		});

		this.prefetchQueue.set(url, {
			url,
			status: 'loading',
			promise,
		});

		this.activeLoads++;
		return promise;
	}

	/**
	 * Process pending queue with concurrency limit
	 */
	private processQueue(): void {
		while (this.activeLoads < this.concurrentLimit && this.pendingQueue.length > 0) {
			const url = this.pendingQueue.shift();
			if (url) {
				this.preloadSvg(url).catch(() => {
					// Errors are already handled in preloadSvg
				});
			}
		}
	}

	/**
	 * Queue a URL for prefetching
	 */
	private queuePrefetch(url: string): void {
		// Skip if already cached
		if (imageCache.isLikelyCached(url)) {
			return;
		}

		// Skip if already queued or loading
		if (this.prefetchQueue.has(url)) {
			return;
		}

		this.prefetchQueue.set(url, {
			url,
			status: 'pending',
		});
		this.pendingQueue.push(url);
		this.processQueue();
	}

	/**
	 * Prefetch SVG images for a phone number
	 * @param phoneNumber - Phone number (with or without +)
	 * @param types - Types of SVG to prefetch (default: body only)
	 */
	prefetchForPhoneNumber(
		phoneNumber: string,
		types: SvgLoadType[] = ['body']
	): void {
		for (const type of types) {
			const url = this.getSvgUrl(phoneNumber, type);
			if (url) {
				this.queuePrefetch(url);
			}
		}
	}

	/**
	 * Prefetch SVG images for multiple phone numbers
	 * @param phoneNumbers - Array of phone numbers (msisdn)
	 * @param types - Types of SVG to prefetch
	 */
	prefetchForPhoneNumbers(
		phoneNumbers: string[],
		types: SvgLoadType[] = ['body']
	): void {
		for (const phoneNumber of phoneNumbers) {
			this.prefetchForPhoneNumber(phoneNumber, types);
		}
	}

	/**
	 * Check if SVG for a phone number is cached
	 */
	isCached(phoneNumber: string, type: SvgLoadType = 'body'): boolean {
		const url = this.getSvgUrl(phoneNumber, type);
		if (!url) return false;
		return imageCache.isLikelyCached(url);
	}

	/**
	 * Wait for a specific phone number SVG to be loaded
	 */
	async waitForSvg(
		phoneNumber: string,
		type: SvgLoadType = 'body'
	): Promise<boolean> {
		const url = this.getSvgUrl(phoneNumber, type);
		if (!url) return false;

		if (imageCache.isLikelyCached(url)) {
			return true;
		}

		const entry = this.prefetchQueue.get(url);
		if (entry?.promise) {
			try {
				await entry.promise;
				return true;
			} catch {
				return false;
			}
		}

		this.queuePrefetch(url);
		const newEntry = this.prefetchQueue.get(url);
		if (newEntry?.promise) {
			try {
				await newEntry.promise;
				return true;
			} catch {
				return false;
			}
		}

		return false;
	}

	/**
	 * Clear prefetch queue and cache
	 */
	clear(): void {
		this.prefetchQueue.clear();
		this.pendingQueue = [];
		this.activeLoads = 0;
	}

	/**
	 * Get prefetch statistics
	 */
	getStats(): {
		queued: number;
		loading: number;
		loaded: number;
		errors: number;
	} {
		let queued = 0;
		let loading = 0;
		let loaded = 0;
		let errors = 0;

		for (const entry of this.prefetchQueue.values()) {
			switch (entry.status) {
				case 'pending':
					queued++;
					break;
				case 'loading':
					loading++;
					break;
				case 'loaded':
					loaded++;
					break;
				case 'error':
					errors++;
					break;
			}
		}

		return { queued, loading, loaded, errors };
	}
}

export const svgPrefetch = new SvgPrefetchManager();

